import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, Button, TextField, Typography,
    Stack, Table, TableHead, TableBody, TableRow, TableCell, IconButton
} from '@mui/material';
import * as api from './psychologicalPricesApi';
import type { PsychologicalPrice, PriceInterval } from './psychologicalPricesApi';
import { useDetailView } from '../common/useDetailView';
import { sx14, FormRow, DetailViewShell } from '../common/detailView';

interface Props {
    psychologicalPriceId: number | null;
}

const BLANK: PriceInterval = { minValue: 0, maxValue: 0, resultValue: 0 };

export function PsychologicalPriceView({ psychologicalPriceId }: Props) {
    const { t } = useTranslation();
    const detail = useDetailView<PsychologicalPrice>();

    const [newInterval, setNewInterval] = useState<PriceInterval>({ ...BLANK });

    const load = useCallback(async () => {
        await detail.withLoading(async () => {
            if (psychologicalPriceId === null) {
                detail.setItem({ id: 0, name: '', description: '', priceIntervals: [] });
                detail.setIsNew(true);
                detail.setIsEditing(true);
            } else {
                const data = await api.getPsychologicalPrice(psychologicalPriceId);
                data.priceIntervals = data.priceIntervals ?? [];
                detail.setItem(data);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            }
        });
    }, [psychologicalPriceId]);

    useEffect(() => { load(); }, [load]);

    const item = detail.item;

    const isNameValid = !!item && !!item.name && item.name.length > 0;
    const hasIntervals = !!item && item.priceIntervals.length > 0;
    // Legacy requires both name and at least one interval when creating;
    // existing records can save with any state.
    const canSave = !detail.isNew || (isNameValid && hasIntervals);

    const cancelEdit = () => {
        detail.cancelEdit();
        setNewInterval({ ...BLANK });
    };

    const save = async () => {
        if (!item) return;
        await detail.withSaving(async () => {
            const response = detail.isNew
                ? await api.createPsychologicalPrice(item)
                : await api.updatePsychologicalPrice(item);
            response.priceIntervals = response.priceIntervals ?? [];
            detail.setItem(response);
            detail.setIsNew(false);
            detail.setIsEditing(false);
            setNewInterval({ ...BLANK });
        });
    };

    const updateInterval = (index: number, patch: Partial<PriceInterval>) => {
        if (!item) return;
        detail.update('priceIntervals', item.priceIntervals.map((iv, i) => i === index ? { ...iv, ...patch } : iv));
    };
    const removeInterval = (index: number) => {
        if (!item) return;
        detail.update('priceIntervals', item.priceIntervals.filter((_, i) => i !== index));
    };
    const clearIntervals = () => {
        if (!item) return;
        detail.update('priceIntervals', []);
        setNewInterval({ ...BLANK });
    };

    const addInterval = () => {
        if (!item) return;
        const { minValue, maxValue, resultValue } = newInterval;
        const min = Number(minValue);
        const max = Number(maxValue);
        if (Number.isNaN(min)) { detail.setError(t('psychologicalPrice.minValueIsWrong')); return; }
        if (Number.isNaN(max)) { detail.setError(t('psychologicalPrice.maxValueIsWrong')); return; }
        if (min > max) { detail.setError(t('psychologicalPrice.minValueLargerThanMaxValue')); return; }

        const inserted: PriceInterval = { minValue: min, maxValue: max, resultValue: Number(resultValue) };
        detail.update('priceIntervals', [...item.priceIntervals, inserted]);

        // Mirror the legacy "suggest next row" behavior: shift by the interval width.
        const diff = max - min;
        setNewInterval({
            minValue: max,
            maxValue: max + diff,
            resultValue: Number(resultValue) + diff,
        });
    };

    return (
        <DetailViewShell<PsychologicalPrice>
            title={item?.name ?? ''}
            isEditing={detail.isEditing}
            isNew={detail.isNew}
            saving={detail.saving}
            canSave={canSave}
            loading={detail.loading}
            error={detail.error}
            item={item}
            onEdit={detail.startEdit}
            onSave={save}
            onCancel={cancelEdit}
            onClearError={() => detail.setError(null)}
        >
            {(pp) => {
                const sortedIntervals = detail.isEditing
                    ? pp.priceIntervals.map((iv, i) => ({ iv, i }))
                    : [...pp.priceIntervals]
                        .map((iv, i) => ({ iv, i }))
                        .sort((a, b) => a.iv.minValue - b.iv.minValue);

                return (<>
                    <FormRow label={t('psychologicalPrice.name')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx14}>{pp.name}</Typography>
                        ) : (
                            <TextField size="small" fullWidth
                                value={pp.name ?? ''}
                                onChange={e => detail.update('name', e.target.value)}
                                error={!isNameValid} />
                        )}
                    </FormRow>

                    <FormRow label={t('psychologicalPrice.description')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx14}>{pp.description}</Typography>
                        ) : (
                            <TextField size="small" fullWidth
                                value={pp.description ?? ''}
                                onChange={e => detail.update('description', e.target.value)} />
                        )}
                    </FormRow>

                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ ...sx14, fontWeight: 'bold', textAlign: 'right' }}>{t('common.min')}</TableCell>
                                <TableCell sx={{ ...sx14, fontWeight: 'bold', textAlign: 'right' }}>{t('common.max')}</TableCell>
                                <TableCell sx={{ ...sx14, fontWeight: 'bold', textAlign: 'right' }}>{t('common.value')}</TableCell>
                                {detail.isEditing && (
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        <Button size="small" variant="contained" color="warning"
                                            onClick={clearIntervals} disabled={!hasIntervals}>
                                            {t('common.clear')}
                                        </Button>
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {detail.isEditing && (
                                <TableRow>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        <TextField size="small" type="number"
                                            value={newInterval.minValue}
                                            onChange={e => setNewInterval({ ...newInterval, minValue: e.target.value === '' ? 0 : Number(e.target.value) })}
                                            inputProps={{ style: { textAlign: 'right' } }} />
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        <TextField size="small" type="number"
                                            value={newInterval.maxValue}
                                            onChange={e => setNewInterval({ ...newInterval, maxValue: e.target.value === '' ? 0 : Number(e.target.value) })}
                                            inputProps={{ style: { textAlign: 'right' } }} />
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        <TextField size="small" type="number"
                                            value={newInterval.resultValue}
                                            onChange={e => setNewInterval({ ...newInterval, resultValue: e.target.value === '' ? 0 : Number(e.target.value) })}
                                            inputProps={{ style: { textAlign: 'right' } }} />
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        <Button size="small" variant="contained" color="secondary" onClick={addInterval}>
                                            {t('common.add')}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            )}

                            {sortedIntervals.map(({ iv, i }) => (
                                <TableRow key={i}>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        {!detail.isEditing ? (
                                            <Typography sx={sx14}>{iv.minValue.toFixed(2)}</Typography>
                                        ) : (
                                            <TextField size="small" type="number"
                                                value={iv.minValue}
                                                onChange={e => updateInterval(i, { minValue: e.target.value === '' ? 0 : Number(e.target.value) })}
                                                inputProps={{ style: { textAlign: 'right' } }} />
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        {!detail.isEditing ? (
                                            <Typography sx={sx14}>{iv.maxValue.toFixed(2)}</Typography>
                                        ) : (
                                            <TextField size="small" type="number"
                                                value={iv.maxValue}
                                                onChange={e => updateInterval(i, { maxValue: e.target.value === '' ? 0 : Number(e.target.value) })}
                                                inputProps={{ style: { textAlign: 'right' } }} />
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        {!detail.isEditing ? (
                                            <Typography sx={sx14}>{iv.resultValue.toFixed(2)}</Typography>
                                        ) : (
                                            <TextField size="small" type="number"
                                                value={iv.resultValue}
                                                onChange={e => updateInterval(i, { resultValue: e.target.value === '' ? 0 : Number(e.target.value) })}
                                                inputProps={{ style: { textAlign: 'right' } }} />
                                        )}
                                    </TableCell>
                                    {detail.isEditing && (
                                        <TableCell sx={{ textAlign: 'right' }}>
                                            <IconButton size="small" color="error" onClick={() => removeInterval(i)}>×</IconButton>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </>);
            }}
        </DetailViewShell>
    );
}
